import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';

// Types
import {
  TableProps,
  dataTable,
  SearchbarFilterTypes,
  budgetCategoriesColumnNames,
} from './types';

// Styled components
import {
  ContainerTable,
  FiltersContainer,
  BudgetCategoriesTableContainer,
  TrStyled,
  ColorIndicator,
} from './styles';

import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../summaryTables/SummaryTablesStyles';
import { Typography } from '../../../../Typography';
import { TruncatedText } from '../../../../TruncatedText';

import { budgetCategoriesMocksData } from './mocks';
import SearchBar from '../../../../../inputs/SearchBar';
import { matchesSearch } from '../../../../../../utils/textSearch';

export const BudgetCategoriesManagementTable = ({
  data,
}: TableProps): JSX.Element => {
  const { t } = useTranslation('pages');
  const categories = data?.activeCategories ?? budgetCategoriesMocksData;
  const [categoriesData, setCategoriesData] = useState(categories);

  const [filteredCategories, setFilteredCategories] = useState(false);

  const categoriesColumnHelper = createColumnHelper<dataTable>();

  useEffect(() => {
    setCategoriesData(categories);
  }, [data]);

  const categoryColumns = useMemo(
    () => [
      categoriesColumnHelper.accessor((row) => row.code, {
        id: budgetCategoriesColumnNames.code.id,
        header: () => t(budgetCategoriesColumnNames.code.labelKey),
        cell: (info) => info.getValue(),
      }),
      categoriesColumnHelper.accessor((row) => row.name, {
        id: budgetCategoriesColumnNames.name.id,
        header: () => t(budgetCategoriesColumnNames.name.labelKey),
        cell: (info) => (
          <TruncatedText
            text={info.getValue() || ''}
            maxLength={40}
            size="XS"
          />
        ),
      }),
      categoriesColumnHelper.accessor((row) => row.color, {
        id: budgetCategoriesColumnNames.color.id,
        header: () => t(budgetCategoriesColumnNames.color.labelKey),
        cell: (info) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ColorIndicator color={info.getValue() || '#cccccc'} />
            {info.getValue() || 'Sense color'}
          </div>
        ),
      }),
      categoriesColumnHelper.accessor((row) => row.budgetItemsCount, {
        id: budgetCategoriesColumnNames.budgetItemsCount.id,
        header: () => t(budgetCategoriesColumnNames.budgetItemsCount.labelKey),
        cell: (info) => info.getValue(),
      }),
    ],
    [categoriesData, data]
  );

  const categoriesSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      if (value === '') {
        setCategoriesData(categories);
        setFilteredCategories((prev) => !prev);
      } else {
        const filtered = categories.filter(
          (category) =>
            matchesSearch([category.code, category.name], value)
        );
        setCategoriesData(filtered);
      }
    },
    [categories, filteredCategories]
  );

  const categoriesTable = useReactTable({
    data: categoriesData,
    columns: categoryColumns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  return (
    <ContainerTable>
      <BudgetCategoriesTableContainer>
        <Typography size="L" variant="semiBold">
          {t('management.budgetCategories')}
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => categoriesSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        <TableContainer>
          <Table>
            <Thead>
              {categoriesTable.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Th>
                  ))}
                </tr>
              ))}
            </Thead>
            <Tbody>
              {categoriesTable.getRowModel().rows.map((row) => (
                <TrStyled key={row.id} checked={true} indeterminate={true}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </TrStyled>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </BudgetCategoriesTableContainer>
    </ContainerTable>
  );
};
